import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TradingSimulationControls = ({ 
  simulationStatus, 
  onStartSimulation, 
  onStopSimulation, 
  onResetSimulation,
  selectedBots,
  onBotSelection 
}) => {
  const [showBotSelector, setShowBotSelector] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  const availableBots = [
    { id: 'bot1', name: 'Gold Rush Scalper', strategy: 'Scalping', status: 'active' },
    { id: 'bot2', name: 'Trend Follower Pro', strategy: 'Trend Following', status: 'active' },
    { id: 'bot3', name: 'Mean Reversion Bot', strategy: 'Mean Reversion', status: 'inactive' },
    { id: 'bot4', name: 'Grid Trading Master', strategy: 'Grid Trading', status: 'active' },
    { id: 'bot5', name: 'DCA Accumulator', strategy: 'DCA', status: 'active' }
  ];

  const handleBotToggle = (botId) => {
    const updatedSelection = selectedBots?.includes(botId)
      ? selectedBots?.filter(id => id !== botId)
      : [...selectedBots, botId];
    onBotSelection(updatedSelection);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'running': return 'text-success';
      case 'stopped': return 'text-error';
      case 'paused': return 'text-warning';
      default: return 'text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'running': return 'Play';
      case 'stopped': return 'Square';
      case 'paused': return 'Pause';
      default: return 'Circle';
    }
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-6 trading-shadow">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
            <Icon name="Settings" size={20} className="text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Simulation Controls</h3>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-muted-foreground">Status:</span>
              <div className="flex items-center space-x-1">
                <Icon 
                  name={getStatusIcon(simulationStatus)} 
                  size={12} 
                  className={getStatusColor(simulationStatus)} 
                />
                <span className={`text-sm font-medium capitalize ${getStatusColor(simulationStatus)}`}>
                  {simulationStatus}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Bot Selection */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-medium text-foreground">Selected Bots ({selectedBots?.length})</h4>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowBotSelector(!showBotSelector)}
            iconName={showBotSelector ? "ChevronUp" : "ChevronDown"}
            iconPosition="right"
          >
            Manage Bots
          </Button>
        </div>

        {showBotSelector && (
          <div className="bg-muted/30 rounded-xl p-4 space-y-3">
            {availableBots?.map((bot) => (
              <div key={bot?.id} className="flex items-center justify-between p-3 bg-background rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className={`w-3 h-3 rounded-full ${
                    bot?.status === 'active' ? 'bg-success' : 'bg-muted-foreground'
                  }`} />
                  <div>
                    <p className="font-medium text-foreground">{bot?.name}</p>
                    <p className="text-xs text-muted-foreground">{bot?.strategy}</p>
                  </div>
                </div>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedBots?.includes(bot?.id)}
                    onChange={() => handleBotToggle(bot?.id)}
                    disabled={bot?.status !== 'active'}
                    className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary focus:ring-2"
                  />
                </label>
              </div>
            ))}
          </div>
        )}

        {selectedBots?.length > 0 && !showBotSelector && (
          <div className="flex flex-wrap gap-2">
            {selectedBots?.map((botId) => {
              const bot = availableBots?.find(b => b?.id === botId);
              return (
                <span key={botId} className="px-3 py-1 bg-primary/10 text-primary rounded-lg text-sm font-medium">
                  {bot?.name}
                </span>
              );
            })}
          </div>
        )}
      </div>
      {/* Control Buttons */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {simulationStatus === 'stopped' ? (
          <Button
            variant="default"
            onClick={onStartSimulation}
            disabled={selectedBots?.length === 0}
            iconName="Play"
            iconPosition="left"
            className="w-full"
          >
            Start Simulation
          </Button>
        ) : (
          <Button
            variant="secondary"
            onClick={onStopSimulation}
            iconName="Square"
            iconPosition="left"
            className="w-full"
          >
            Stop Simulation
          </Button>
        )}

        <Button
          variant="outline"
          onClick={onStopSimulation}
          disabled={simulationStatus === 'stopped'}
          iconName="Pause"
          iconPosition="left"
          className="w-full"
        >
          Pause
        </Button>

        <Button
          variant="destructive"
          onClick={() => setShowResetConfirm(true)}
          iconName="RotateCcw"
          iconPosition="left"
          className="w-full"
        >
          Reset
        </Button>
      </div>
      {/* Reset Confirmation Modal */}
      {showResetConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-2xl p-6 max-w-md w-full mx-4 trading-shadow">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-error/10 rounded-xl flex items-center justify-center">
                <Icon name="AlertTriangle" size={20} className="text-error" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">Reset Simulation</h3>
                <p className="text-sm text-muted-foreground">This action cannot be undone</p>
              </div>
            </div>
            
            <p className="text-foreground mb-6">
              Are you sure you want to reset the paper trading simulation? This will:
            </p>
            
            <ul className="space-y-2 mb-6 text-sm text-muted-foreground">
              <li className="flex items-center space-x-2">
                <Icon name="X" size={12} className="text-error" />
                <span>Close all active positions</span>
              </li>
              <li className="flex items-center space-x-2">
                <Icon name="X" size={12} className="text-error" />
                <span>Reset virtual balance to starting capital</span>
              </li>
              <li className="flex items-center space-x-2">
                <Icon name="X" size={12} className="text-error" />
                <span>Clear all trading history</span>
              </li>
            </ul>

            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowResetConfirm(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  onResetSimulation();
                  setShowResetConfirm(false);
                }}
                className="flex-1"
              >
                Reset Simulation
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TradingSimulationControls;